import { useState, useCallback } from "react";

var CRICKET_NUMBERS = [20, 19, 18, 17, 16, 15, 25];

var MAX_CHECKOUT = 170;

// Checkout validation — must end on a double (or bull=50)
function isValidDoubleOut(score) {
  if (score > MAX_CHECKOUT || score < 2) return false;
  // Single bull = 25, not a double. Bull = 50 is valid.
  if (score === 25) return false;
  // All even numbers up to 40 (double 1–20)
  if (score <= 40 && score % 2 === 0) return true;
  // Double 20 = 40, double bull = 50
  if (score === 50) return true;
  // Any other score requires combination — we just check it's achievable
  // Standard validation: score must be reachable ending on a double
  return score <= 170 && score !== 169 && score !== 168 && score !== 166 &&
    score !== 165 && score !== 163 && score !== 162 && score !== 159;
}

function initX01Player(name, start) {
  return { name, score: start, darts: 0, visits: [], highestCheckout: 0, tons: 0, ton40s: 0, ton80s: 0 };
}

function initCricketPlayer(name) {
  return {
    name,
    marks: { 20:0, 19:0, 18:0, 17:0, 16:0, 15:0, 25:0 },
    points: 0,
  };
}

function calcAvg(player) {
  if (player.darts === 0) return "0.00";
  var total = player.visits.reduce(function(s, v) { return s + v; }, 0);
  return ((total / player.darts) * 3).toFixed(2);
}

var col = {
  bg: "#1a1a1a",
  card: "#252525",
  cardActive: "#2a3a1a",
  border: "#333",
  borderActive: "#6DC878",
  text: "#e8e8e8",
  textMuted: "#888",
  textActive: "#6DC878",
  amber: "#FFB020",
  amberBg: "#2a2000",
  amberBorder: "#664400",
  red: "#FF6B6B",
  redBg: "#2a0000",
  blue: "#7BA7FF",
  green: "#6DC878",
  greenBg: "#0a2010",
  greenBorder: "#2a5030",
};

var btnBase = { border: "none", borderRadius: 8, padding: "10px 20px", fontSize: 14, cursor: "pointer", fontFamily: "sans-serif", fontWeight: 500, transition: "opacity 0.1s" };
var btnGreen = { ...btnBase, background: col.green, color: "#0a2010" };
var btnGhost = { ...btnBase, background: "#333", color: col.text, border: "1px solid #444" };

function OptionBtn({ label, active, onClick }) {
  return (
    <button onClick={onClick} style={{ ...btnBase, padding: "9px 0", width: "100%",
      background: active ? col.green : "#2a2a2a",
      color: active ? "#0a2010" : col.textMuted,
      border: active ? "2px solid " + col.green : "2px solid #333",
      fontWeight: active ? 700 : 400,
    }}>{label}</button>
  );
}

function StatRow({ label, value }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "0.5px solid #333", fontSize: 13 }}>
      <span style={{ color: col.textMuted }}>{label}</span>
      <span style={{ color: col.text, fontWeight: 500 }}>{value}</span>
    </div>
  );
}

export default function ScorePage({ user }) {
  var [screen, setScreen] = useState("setup");
  var [gameType, setGameType] = useState("501"); // "301"|"501"|"701"|"cricket"
  var [outRule, setOutRule] = useState("double");
  var [p1name, setP1name] = useState(user && user.playerName ? user.playerName.split(",").reverse().map(function(s){return s.trim();}).join(" ") : "Player 1");
  var [p2name, setP2name] = useState("Player 2");
  var [legs, setLegs] = useState(1);

  // X01 game state
  var [players, setPlayers] = useState(null);
  var [current, setCurrent] = useState(0);
  var [inputVal, setInputVal] = useState("");
  var [dartsThisVisit, setDartsThisVisit] = useState(3);
  var [inputError, setInputError] = useState("");
  var [legWins, setLegWins] = useState([0, 0]);
  var [currentLeg, setCurrentLeg] = useState(1);
  var [history, setHistory] = useState([]);

  // Cricket state
  var [cricketPlayers, setCricketPlayers] = useState(null);
  var [cricketCurrent, setCricketCurrent] = useState(0);
  // cricketVisit: { 20:0, 19:0, ... } — marks hit this visit per number
  var [cricketVisit, setCricketVisit] = useState({});
  var [cricketHistory, setCricketHistory] = useState([]);

  var startScore = parseInt(gameType, 10) || 501;
  var isCricket = gameType === "cricket";

  var startGame = useCallback(function() {
    if (isCricket) {
      setCricketPlayers([initCricketPlayer(p1name), initCricketPlayer(p2name)]);
      setCricketCurrent(0);
      setCricketVisit({});
      setCricketHistory([]);
      setLegWins([0, 0]);
      setScreen("cricket");
    } else {
      var s = startScore;
      setPlayers([initX01Player(p1name, s), initX01Player(p2name, s)]);
      setCurrent(0);
      setInputVal("");
      setInputError("");
      setDartsThisVisit(3);
      setLegWins([0, 0]);
      setCurrentLeg(1);
      setHistory([]);
      setScreen("game");
    }
  }, [p1name, p2name, startScore, isCricket]);

  function submitX01Score() {
    var val = parseInt(inputVal, 10);
    if (isNaN(val) || val < 0 || val > 180) { setInputError("Score must be between 0 and 180."); return; }
    var p = players[current];
    var newScore = p.score - val;
    if (newScore < 0) { setInputError("Bust — score goes below zero."); return; }
    if (newScore === 1) { setInputError("Bust — can't finish on 1."); return; }
    if (outRule === "double" && newScore === 0 && !isValidDoubleOut(val)) {
      setInputError("Invalid finish — must end on a double or bull (50). Max checkout is 170."); return;
    }
    var darts = dartsThisVisit;
    var isCheckout = newScore === 0;
    var updated = {
      ...p, score: newScore, darts: p.darts + darts,
      visits: [...p.visits, val],
      tons: p.tons + (val >= 100 && val < 140 ? 1 : 0),
      ton40s: p.ton40s + (val >= 140 && val < 180 ? 1 : 0),
      ton80s: p.ton80s + (val === 180 ? 1 : 0),
      highestCheckout: isCheckout ? Math.max(p.highestCheckout, val) : p.highestCheckout,
    };
    setHistory(function(h) { return [...h, { player: current, score: val, darts }]; });
    setInputVal(""); setInputError("");
    if (isCheckout) {
      var newWins = legWins.map(function(w, i) { return i === current ? w + 1 : w; });
      var updatedAll = players.map(function(pl, i) { return i === current ? updated : pl; });
      setPlayers(updatedAll);
      setLegWins(newWins);
      if (newWins[current] >= Math.ceil(legs / 2)) {
        setScreen("result");
      } else {
        setTimeout(function() {
          setPlayers([initX01Player(p1name, startScore), initX01Player(p2name, startScore)]);
          setCurrentLeg(function(l) { return l + 1; });
          setCurrent(function(c) { return c === 0 ? 1 : 0; });
          setHistory([]);
        }, 400);
      }
    } else {
      setPlayers(function(prev) { return prev.map(function(pl, i) { return i === current ? updated : pl; }); });
      setCurrent(function(c) { return c === 0 ? 1 : 0; });
    }
  }

  function undoX01() {
    if (history.length === 0) return;
    var last = history[history.length - 1];
    setPlayers(function(prev) {
      return prev.map(function(pl, i) {
        if (i !== last.player) return pl;
        var prevVisits = pl.visits.slice(0, -1);
        return { ...pl, score: pl.score + last.score, darts: pl.darts - last.darts, visits: prevVisits,
          tons: prevVisits.filter(function(v){return v>=100&&v<140;}).length,
          ton40s: prevVisits.filter(function(v){return v>=140&&v<180;}).length,
          ton80s: prevVisits.filter(function(v){return v===180;}).length,
        };
      });
    });
    setCurrent(last.player);
    setHistory(function(h) { return h.slice(0, -1); });
    setInputVal(""); setInputError("");
  }

  function submitCricket() {
    var p = cricketPlayers[cricketCurrent];
    var opp = cricketPlayers[cricketCurrent === 0 ? 1 : 0];
    var newPlayerMarks = { ...p.marks };
    var totalPointsAdded = 0;
    var visitSnapshot = {};
    CRICKET_NUMBERS.forEach(function(num) {
      var marksToAdd = cricketVisit[num] || 0;
      visitSnapshot[num] = marksToAdd;
      var prevMarks = newPlayerMarks[num];
      var afterMarks = Math.min(prevMarks + marksToAdd, 3);
      var scored = Math.max(0, (prevMarks + marksToAdd) - 3);
      var pointsForNum = opp.marks[num] < 3 ? scored * num : 0;
      newPlayerMarks[num] = afterMarks;
      totalPointsAdded += pointsForNum;
    });
    setCricketHistory(function(h) {
      return [...h, { player: cricketCurrent, visitSnapshot: visitSnapshot, pointsAdded: totalPointsAdded, prevMarks: p.marks, prevPoints: p.points }];
    });
    setCricketPlayers(function(prev) {
      return prev.map(function(pl, i) {
        if (i !== cricketCurrent) return pl;
        return { ...pl, marks: newPlayerMarks, points: pl.points + totalPointsAdded };
      });
    });
    setCricketVisit({});
    var allClosed = CRICKET_NUMBERS.every(function(n) { return newPlayerMarks[n] >= 3; });
    if (allClosed) {
      var currentPoints = p.points + totalPointsAdded;
      var oppPoints = opp.points;
      if (currentPoints >= oppPoints) {
        setScreen("result");
        return;
      }
    }
    setCricketCurrent(function(c) { return c === 0 ? 1 : 0; });
  }

  function undoCricket() {
    if (cricketHistory.length === 0) return;
    var last = cricketHistory[cricketHistory.length - 1];
    setCricketPlayers(function(prev) {
      return prev.map(function(pl, i) {
        if (i !== last.player) return pl;
        return { ...pl, marks: last.prevMarks, points: last.prevPoints };
      });
    });
    setCricketCurrent(last.player);
    setCricketHistory(function(h) { return h.slice(0, -1); });
    setCricketVisit({});
  }

  var inputBoxStyle = { background: "#2a2a2a", border: "2px solid #444", borderRadius: 8, color: col.text, padding: "12px 16px", fontSize: 18, outline: "none", width: "100%", boxSizing: "border-box", textAlign: "center", fontFamily: "sans-serif" };
  var labelStyle = { fontSize: 11, color: col.textMuted, marginBottom: 6, textTransform: "uppercase", letterSpacing: 1, display: "block" };
  var sectionStyle = { marginBottom: 20 };

  // ── Setup ──────────────────────────────────────────────────────────────────
  if (screen === "setup") {
    return (
      <div style={{ maxWidth: 480, margin: "0 auto", padding: "24px 16px 60px", fontFamily: "sans-serif", color: col.text, background: col.bg, minHeight: "80vh" }}>
        <h2 style={{ textAlign: "center", fontWeight: 700, fontSize: 22, margin: "0 0 24px", color: col.amber }}>New game</h2>

        <div style={{ background: col.card, borderRadius: 12, padding: 20, border: "1px solid #333" }}>

          <div style={sectionStyle}>
            <label style={labelStyle}>Player 1</label>
            <input style={inputBoxStyle} value={p1name} onChange={function(e){setP1name(e.target.value);}} />
          </div>

          <div style={sectionStyle}>
            <label style={labelStyle}>Player 2</label>
            <input style={inputBoxStyle} value={p2name} onChange={function(e){setP2name(e.target.value);}} />
          </div>

          <div style={sectionStyle}>
            <label style={labelStyle}>Format</label>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 8 }}>
              {["301","501","701","cricket"].map(function(f) {
                return <OptionBtn key={f} label={f === "cricket" ? "Cricket" : f} active={gameType === f} onClick={function(){setGameType(f);}} />;
              })}
            </div>
          </div>

          {gameType !== "cricket" && (
            <div style={sectionStyle}>
              <label style={labelStyle}>Finish</label>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                <OptionBtn label="Double out" active={outRule === "double"} onClick={function(){setOutRule("double");}} />
                <OptionBtn label="Straight out" active={outRule === "straight"} onClick={function(){setOutRule("straight");}} />
              </div>
            </div>
          )}

          <div style={sectionStyle}>
            <label style={labelStyle}>Best of</label>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 8 }}>
              {[1,3,5,7].map(function(n){
                return <OptionBtn key={n} label={String(n)} active={legs === n} onClick={function(){setLegs(n);}} />;
              })}
            </div>
          </div>

          <button style={{ ...btnGreen, width: "100%", padding: "13px", fontSize: 16 }} onClick={startGame}>Start game</button>
        </div>
      </div>
    );
  }

  // ── X01 Game ───────────────────────────────────────────────────────────────
  if (screen === "game" && players) {
    var p = players[current];
    var showCheckout = p.score <= MAX_CHECKOUT && p.score >= 2 && outRule === "double";
    var quickScores = [26, 41, 45, 60, 85, 100, 121, 140, 180];

    return (
      <div style={{ maxWidth: 500, margin: "0 auto", padding: "12px 14px 60px", fontFamily: "sans-serif", color: col.text, background: col.bg, minHeight: "80vh" }}>

        <div style={{ textAlign: "center", fontSize: 12, color: col.textMuted, marginBottom: 12 }}>
          {gameType} · {outRule === "double" ? "double out" : "straight out"} · leg {currentLeg}/{legs}
          {(legWins[0] > 0 || legWins[1] > 0) && <span style={{ marginLeft: 8 }}>{p1name.split(" ")[0]} {legWins[0]}–{legWins[1]} {p2name.split(" ")[0]}</span>}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
          {players.map(function(pl, pi) {
            var isActive = pi === current;
            return (
              <div key={pi} style={{ background: isActive ? col.amberBg : col.card, border: "2px solid " + (isActive ? col.amber : "#333"), borderRadius: 12, padding: "14px 10px", textAlign: "center" }}>
                <div style={{ fontSize: 12, color: isActive ? col.amber : col.textMuted, fontWeight: isActive ? 700 : 400, marginBottom: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {isActive ? "▶ " : ""}{pl.name}
                </div>
                <div style={{ fontSize: 52, fontWeight: 800, color: isActive ? col.amber : col.text, lineHeight: 1, marginBottom: 4 }}>{pl.score}</div>
                <div style={{ fontSize: 12, color: col.textMuted }}>avg {calcAvg(pl)}</div>
                <div style={{ fontSize: 11, color: col.textMuted }}>{pl.darts} darts</div>
                {(pl.ton80s > 0 || pl.ton40s > 0 || pl.tons > 0) && (
                  <div style={{ fontSize: 11, color: col.blue, marginTop: 3 }}>
                    {pl.ton80s > 0 ? "180×"+pl.ton80s+" " : ""}{pl.ton40s > 0 ? "140+×"+pl.ton40s+" " : ""}{pl.tons > 0 ? "100+×"+pl.tons : ""}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {showCheckout && (
          <div style={{ background: col.greenBg, border: "1px solid " + col.greenBorder, borderRadius: 8, padding: "8px 14px", marginBottom: 10, fontSize: 13, color: col.green, textAlign: "center", fontWeight: 700 }}>
            Checkout: {p.score}
          </div>
        )}

        {inputError && (
          <div style={{ background: col.redBg, border: "1px solid #660000", borderRadius: 8, padding: "8px 14px", marginBottom: 10, fontSize: 13, color: col.red, textAlign: "center" }}>
            {inputError}
          </div>
        )}

        <div style={{ marginBottom: 10 }}>
          <label style={labelStyle}>{p.name}'s score this visit</label>
          <input style={{ ...inputBoxStyle, border: "2px solid " + col.amber, fontSize: 22 }}
            type="number" value={inputVal} min="0" max="180"
            onChange={function(e){ setInputVal(e.target.value); setInputError(""); }}
            onKeyDown={function(e){ if (e.key === "Enter") submitX01Score(); }}
            autoFocus
          />
        </div>

        <div style={{ marginBottom: 10 }}>
          <label style={labelStyle}>Darts thrown this visit</label>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
            {[1,2,3].map(function(n){
              return <OptionBtn key={n} label={n + " dart" + (n>1?"s":"")} active={dartsThisVisit===n} onClick={function(){setDartsThisVisit(n);}} />;
            })}
          </div>
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>Quick scores</label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {quickScores.map(function(qs){
              return (
                <button key={qs} onClick={function(){ setInputVal(String(qs)); setInputError(""); }} style={{ background: "#333", border: "1px solid #555", borderRadius: 6, color: col.text, padding: "6px 12px", fontSize: 13, cursor: "pointer" }}>
                  {qs}
                </button>
              );
            })}
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 10 }}>
          <button style={{ ...btnGhost, padding: "12px" }} onClick={undoX01}>Undo</button>
          <button style={{ ...btnGreen, padding: "12px", fontSize: 16 }} onClick={submitX01Score}>Submit</button>
        </div>

        <button style={{ ...btnGhost, width: "100%", padding: "10px", fontSize: 12, marginTop: 4 }} onClick={function(){ setScreen("setup"); }}>Abandon game</button>
      </div>
    );
  }

  // ── Cricket ────────────────────────────────────────────────────────────────
  if (screen === "cricket" && cricketPlayers) {
    function markSymbol(n) {
      if (n === 0) return "";
      if (n === 1) return "/";
      if (n === 2) return "X";
      return "✓";
    }

    return (
      <div style={{ maxWidth: 500, margin: "0 auto", padding: "12px 14px 60px", fontFamily: "sans-serif", color: col.text, background: col.bg, minHeight: "80vh" }}>

        <div style={{ textAlign: "center", fontSize: 12, color: col.textMuted, marginBottom: 12 }}>Cricket</div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", gap: 8, marginBottom: 14, alignItems: "center" }}>
          <div style={{ background: cricketCurrent === 0 ? col.amberBg : col.card, border: "2px solid " + (cricketCurrent === 0 ? col.amber : "#333"), borderRadius: 10, padding: "10px", textAlign: "center" }}>
            <div style={{ fontSize: 12, color: cricketCurrent === 0 ? col.amber : col.textMuted, marginBottom: 4 }}>{cricketCurrent === 0 ? "▶ " : ""}{cricketPlayers[0].name}</div>
            <div style={{ fontSize: 32, fontWeight: 800, color: cricketCurrent === 0 ? col.amber : col.text }}>{cricketPlayers[0].points}</div>
          </div>
          <div style={{ textAlign: "center", color: col.textMuted, fontSize: 14, fontWeight: 700 }}>vs</div>
          <div style={{ background: cricketCurrent === 1 ? col.amberBg : col.card, border: "2px solid " + (cricketCurrent === 1 ? col.amber : "#333"), borderRadius: 10, padding: "10px", textAlign: "center" }}>
            <div style={{ fontSize: 12, color: cricketCurrent === 1 ? col.amber : col.textMuted, marginBottom: 4 }}>{cricketCurrent === 1 ? "▶ " : ""}{cricketPlayers[1].name}</div>
            <div style={{ fontSize: 32, fontWeight: 800, color: cricketCurrent === 1 ? col.amber : col.text }}>{cricketPlayers[1].points}</div>
          </div>
        </div>

        <div style={{ background: col.card, border: "1px solid #333", borderRadius: 10, marginBottom: 14, overflow: "hidden" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 60px 1fr", borderBottom: "1px solid #333" }}>
            <div style={{ padding: "8px", textAlign: "center", fontSize: 11, color: col.textMuted, textTransform: "uppercase" }}>{cricketPlayers[0].name.split(" ")[0]}</div>
            <div style={{ padding: "8px", textAlign: "center", fontSize: 11, color: col.textMuted, textTransform: "uppercase", borderLeft: "1px solid #333", borderRight: "1px solid #333" }}>Num</div>
            <div style={{ padding: "8px", textAlign: "center", fontSize: 11, color: col.textMuted, textTransform: "uppercase" }}>{cricketPlayers[1].name.split(" ")[0]}</div>
          </div>
          {CRICKET_NUMBERS.map(function(num) {
            var m0 = cricketPlayers[0].marks[num];
            var m1 = cricketPlayers[1].marks[num];
            var closed = m0 >= 3 && m1 >= 3;
            var numLabel = num === 25 ? "Bull" : String(num);
            var hasVisitMarks = (cricketVisit[num] || 0) > 0;
            return (
              <div key={num} style={{ display: "grid", gridTemplateColumns: "1fr 60px 1fr", borderBottom: "0.5px solid #333", background: hasVisitMarks ? col.amberBg : closed ? "#1a1a1a" : "transparent" }}>
                <div style={{ padding: "10px 8px", textAlign: "center", fontSize: 18, fontWeight: 700, color: m0 >= 3 ? col.green : col.text }}>{markSymbol(m0)}</div>
                <div style={{ padding: "10px 4px", textAlign: "center", fontSize: 14, fontWeight: 700, color: hasVisitMarks ? col.amber : closed ? "#555" : col.text, borderLeft: "1px solid #333", borderRight: "1px solid #333" }}>{numLabel}</div>
                <div style={{ padding: "10px 8px", textAlign: "center", fontSize: 18, fontWeight: 700, color: m1 >= 3 ? col.green : col.text }}>{markSymbol(m1)}</div>
              </div>
            );
          })}
        </div>

        <div style={{ marginBottom: 10 }}>
          <label style={labelStyle}>This visit — tap a number then set marks</label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
            {CRICKET_NUMBERS.map(function(num) {
              var bothClosed = cricketPlayers[0].marks[num] >= 3 && cricketPlayers[1].marks[num] >= 3;
              var thisVisitMarks = cricketVisit[num] || 0;
              var numLabel = num === 25 ? "Bull" : String(num);
              return (
                <div key={num} style={{ flex: "1 1 80px" }}>
                  <div style={{ fontSize: 11, color: col.textMuted, textAlign: "center", marginBottom: 4 }}>{numLabel}</div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 3 }}>
                    {[0,1,2,3].map(function(m){
                      var active = thisVisitMarks === m;
                      return (
                        <button key={m} onClick={function(){ if (!bothClosed) setCricketVisit(function(cv){ return { ...cv, [num]: m }; }); }}
                          style={{ background: active ? col.green : "#2a2a2a", color: active ? "#0a2010" : (bothClosed ? "#444" : col.textMuted),
                            border: "2px solid " + (active ? col.green : "#333"), borderRadius: 6,
                            padding: "6px 0", fontSize: 12, cursor: bothClosed ? "default" : "pointer", fontWeight: active ? 700 : 400 }}>
                          {m === 0 ? "0" : m === 1 ? "/" : m === 2 ? "X" : "✓"}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
          {Object.entries(cricketVisit).filter(function(e){ return e[1] > 0; }).length > 0 && (
            <div style={{ fontSize: 12, color: col.amber, background: col.amberBg, border: "1px solid " + col.amberBorder, borderRadius: 6, padding: "6px 10px", marginTop: 4 }}>
              {"This visit: " + Object.entries(cricketVisit).filter(function(e){ return e[1] > 0; }).map(function(e){ return (e[1]===1?"/":e[1]===2?"X":"✓") + " " + (parseInt(e[0])===25?"Bull":e[0]); }).join(", ")}
            </div>
          )}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 10 }}>
          <button style={{ ...btnGhost, padding: "12px", opacity: cricketHistory.length === 0 ? 0.4 : 1 }} onClick={undoCricket}>Undo</button>
          <button style={{ ...btnGhost, padding: "12px" }} onClick={function(){ setCricketVisit({}); }}>Clear</button>
          <button style={{ ...btnGreen, padding: "12px", fontSize: 16 }} onClick={submitCricket}>Submit</button>
        </div>

        <button style={{ ...btnGhost, width: "100%", padding: "10px", fontSize: 12 }} onClick={function(){ setScreen("setup"); }}>Abandon game</button>
      </div>
    );
  }

  // ── Result ─────────────────────────────────────────────────────────────────
  if (screen === "result") {
    var winnerIdx = isCricket
      ? (cricketPlayers[0].points >= cricketPlayers[1].points ? 0 : 1)
      : (legWins[0] > legWins[1] ? 0 : 1);
    var winnerName = isCricket ? cricketPlayers[winnerIdx].name : players[winnerIdx].name;

    return (
      <div style={{ maxWidth: 500, margin: "0 auto", padding: "24px 16px 60px", fontFamily: "sans-serif", color: col.text, background: col.bg, minHeight: "80vh" }}>
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <div style={{ fontSize: 48, marginBottom: 8 }}>🏆</div>
          <h2 style={{ margin: "0 0 4px", fontSize: 28, fontWeight: 800, color: col.amber }}>{winnerName} wins!</h2>
          {!isCricket && <div style={{ fontSize: 14, color: col.textMuted }}>{legWins[0]} – {legWins[1]}</div>}
        </div>

        {!isCricket && players && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            {players.map(function(pl, pi) {
              var isWinner = pi === winnerIdx;
              return (
                <div key={pi} style={{ background: col.card, border: "2px solid " + (isWinner ? col.amber : "#333"), borderRadius: 12, padding: 16 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: isWinner ? col.amber : col.text, marginBottom: 12 }}>{pl.name}</div>
                  <StatRow label="Average" value={calcAvg(pl)} />
                  <StatRow label="Darts thrown" value={pl.darts} />
                  <StatRow label="Highest checkout" value={pl.highestCheckout || "-"} />
                  <StatRow label="180s" value={pl.ton80s} />
                  <StatRow label="140+" value={pl.ton40s} />
                  <StatRow label="100+" value={pl.tons} />
                </div>
              );
            })}
          </div>
        )}

        {isCricket && cricketPlayers && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            {cricketPlayers.map(function(pl, pi) {
              var isWinner = pi === winnerIdx;
              return (
                <div key={pi} style={{ background: col.card, border: "2px solid " + (isWinner ? col.amber : "#333"), borderRadius: 12, padding: 16 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: isWinner ? col.amber : col.text, marginBottom: 12 }}>{pl.name}</div>
                  <StatRow label="Points scored" value={pl.points} />
                  {CRICKET_NUMBERS.map(function(n) {
                    return <StatRow key={n} label={n === 25 ? "Bull" : String(n)} value={pl.marks[n] >= 3 ? "✓ Closed" : pl.marks[n] + " marks"} />;
                  })}
                </div>
              );
            })}
          </div>
        )}

        <button style={{ ...btnGreen, width: "100%", padding: "13px", fontSize: 16, marginTop: 20 }} onClick={function(){ setScreen("setup"); }}>Play again</button>
      </div>
    );
  }

  return null;
}
